import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, AlertTriangle, Calendar, ShieldCheck, 
  BookOpen, ChevronDown, Bell, Clock 
} from 'lucide-react';
import api from '../../connector/api';

const VIOLATION_OPTIONS = [
  "No ID Card",
  "No Tie",
  "No PE Uniform",
  "Wrong Shoes",
  "Wrong Hair Color",
  "No School Logo",
  "Wrong Shirt Sleeves",
  "Wrong ID string"
];

const INTERVENTION_OPTIONS = [
  { key: 'verbal', label: 'Verbal Warning' },
  { key: 'telegram', label: 'Telegram Alert' },
  { key: 'call', label: 'Phone Call' },
  { key: 'detention', label: 'Detention' }
];

// Helper to safely extract an ID whether it's nested or direct
const getId = (val) => {
  if (!val) return '';
  if (typeof val === 'object') {
    if (val.subject && typeof val.subject === 'object') return String(val.subject.id);
    if (val.subject_id) return String(val.subject_id);
    return String(val.id);
  }
  return String(val);
};

export default function NewViolationModal({ 
  isOpen, 
  onClose, 
  onSave, 
  studentId, 
  classroomId,
  studentName, 
  infractionData = null 
}) {
  // Form State
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedViolations, setSelectedViolations] = useState([]);
  const [interventions, setInterventions] = useState(['verbal']);
  const [notes, setNotes] = useState('');
  const [parentAlerted, setParentAlerted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Metadata State
  const [subjects, setSubjects] = useState([]);
  const [activeTerm, setActiveTerm] = useState(null);
  const [isSubjectOpen, setIsSubjectOpen] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);

  // DateTime Helper
  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };
  const [date, setDate] = useState(getCurrentDateTime());

  const subjectDropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (subjectDropdownRef.current && !subjectDropdownRef.current.contains(e.target)) {
        setIsSubjectOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch teacher-assigned subjects & active term
  useEffect(() => {
    if (isOpen) {
      const fetchMetadata = async () => {
        setIsLoadingSubjects(true);
        try {
          const [teacherSubjectsRes, termRes] = await Promise.all([
            api.get('/academic/teacher-assigned-subjects/'),
            api.get('/year-terms/')
          ]);

          if (teacherSubjectsRes.data) {
            const assignmentList = teacherSubjectsRes.data.results || teacherSubjectsRes.data;

            const normalizedSubjects = assignmentList.map(s => {
              const calculatedId = getId(s);
              let label = s.display_name || s.custom_name || s.name || s.subject_name;
              if (!label && s.subject && typeof s.subject === 'object') {
                label = s.subject.name || s.subject.display_name;
              }
              
              return {
                ...s,
                id: calculatedId,
                name: label || `Subject #${calculatedId}`
              };
            });

            setSubjects(normalizedSubjects);
          }

          if (termRes.data) {
            const terms = termRes.data.results || termRes.data;
            const current = terms.find(t => t.is_current || t.is_active || t.active) || terms[0];
            if (current) setActiveTerm(current);
          }
        } catch (err) {
          console.error("❌ Failed to load metadata:", err);
          setSubjects([]);
        } finally {
          setIsLoadingSubjects(false);
        }
      };

      fetchMetadata();
    }
  }, [isOpen]);

  // Populate data when editing vs creating
  useEffect(() => {
    if (infractionData && isOpen) {
      setSelectedSubjectId(getId(infractionData.subject));
      setSelectedViolations(infractionData.specific_item ? infractionData.specific_item.split(', ') : []);
      
      if (infractionData.date_of_infraction) {
        const d = new Date(infractionData.date_of_infraction);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        setDate(d.toISOString().slice(0, 16));
      }

      setInterventions(infractionData.intervention_applied ? infractionData.intervention_applied.split(', ') : ['verbal']);
      
      const noteStr = infractionData.detailed_note || '';
      const hasAlert = noteStr.includes('[PARENT ALERTED ADMINISTRATION]');
      setParentAlerted(hasAlert);
      setNotes(hasAlert ? noteStr.replace('[PARENT ALERTED ADMINISTRATION] ', '') : noteStr);
    } else {
      setSelectedSubjectId('');
      setSelectedViolations([]);
      setDate(getCurrentDateTime());
      setInterventions(['verbal']);
      setNotes('');
      setParentAlerted(false);
    }
  }, [infractionData, isOpen]);

  const toggleViolation = (item) => {
    setSelectedViolations(prev => 
      prev.includes(item) ? prev.filter(v => v !== item) : [...prev, item]
    );
  };

  const toggleIntervention = (key) => {
    setInterventions(prev => 
      prev.includes(key) ? prev.filter(i => i !== key) : [...prev, key]
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!studentId || selectedViolations.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const payload = {
        student: parseInt(studentId, 10),
        classroom: classroomId ? parseInt(classroomId, 10) : null,
        subject: selectedSubjectId ? parseInt(selectedSubjectId, 10) : null,
        school_year_term: typeof activeTerm === 'object' ? activeTerm?.id : activeTerm,
        specific_item: selectedViolations.join(', '),
        date_of_infraction: new Date(date).toISOString(),
        intervention_applied: interventions.join(', ') || 'verbal',
        detailed_note: parentAlerted ? `[PARENT ALERTED ADMINISTRATION] ${notes}`.trim() : notes.trim()
      };

      await onSave(payload, infractionData?.id || null);
      onClose();
    } catch (err) {
      console.error("Submission failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderString = (val) => {
    if (!val) return '';
    if (typeof val === 'string' || typeof val === 'number') return val;
    if (typeof val === 'object') return val.display_name || val.name || val.term_name || '';
    return String(val);
  };

  const getSubjectDisplayName = (s) => {
    if (!s) return '';
    return s.display_name || s.name || s.custom_name || s.subject_name || s.subject?.name || 'Subject';
  };

  const activeSubject = subjects.find(s => String(getId(s)) === String(selectedSubjectId));

  const displayTermName = useMemo(() => {
    if (!activeTerm) return 'Loading...';
    if (typeof activeTerm === 'string' || typeof activeTerm === 'number') {
      return `Term #${activeTerm}`;
    }
    const termName = activeTerm.term?.name || activeTerm.term_name || activeTerm.name || activeTerm.title;
    const schoolYear = activeTerm.school_year?.name || activeTerm.school_year_name || '';
    return termName && schoolYear ? `${termName} (${schoolYear})` : renderString(termName) || 'Current Term';
  }, [activeTerm]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0.1 }}
            className="relative bg-white w-full max-w-2xl rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col z-10 font-dynamic-title"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />

            {/* Header */}
            <div className="px-6 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg ${infractionData ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-900 leading-tight">
                    {infractionData ? 'Modify Infraction' : 'Log Violation'}
                  </h2>
                  <p className="text-[11px] text-slate-500">
                    Student: <span className="font-semibold text-slate-700">{renderString(studentName) || (studentId ? `#${studentId}` : 'N/A')}</span>
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 transition cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} id="student-infraction-form" className="flex-1 overflow-y-auto p-5 space-y-3.5">
              
              {/* Row 1: Subject Select & Date Picker Side-by-Side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Classroom Subject Dropdown */}
                <div className="space-y-1 relative" ref={subjectDropdownRef}>
                  <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Subject / Class Period</label>
                  <button
                    type="button"
                    onClick={() => setIsSubjectOpen(!isSubjectOpen)}
                    disabled={isLoadingSubjects}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs flex items-center justify-between h-9 focus:ring-2 focus:ring-slate-400 focus:outline-none transition disabled:opacity-60 cursor-pointer"
                  >
                    <span className="flex items-center gap-2 text-slate-800 truncate">
                      <BookOpen className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      {isLoadingSubjects 
                        ? "Loading your subjects..." 
                        : activeSubject 
                          ? getSubjectDisplayName(activeSubject) 
                          : "Select class subject..."}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  </button>

                  {isSubjectOpen && (
                    <div className="absolute left-0 right-0 mt-1 max-h-44 bg-white border border-slate-200 rounded-xl shadow-lg overflow-y-auto z-30 py-1">
                      <button
                        type="button"
                        onClick={() => { setSelectedSubjectId(''); setIsSubjectOpen(false); }}
                        className="w-full text-left px-3 py-1.5 text-xs italic text-slate-400 hover:bg-slate-50 transition cursor-pointer"
                      >
                        General / Outside Class
                      </button>
                      {subjects.length === 0 ? (
                        <div className="px-3 py-1.5 text-xs text-slate-400 italic">
                          {isLoadingSubjects ? 'Loading subjects...' : 'No subjects assigned to you for this term'}
                        </div>
                      ) : (
                        subjects.map(s => {
                          const sid = getId(s);
                          return (
                            <button
                              key={sid}
                              type="button"
                              onClick={() => { setSelectedSubjectId(sid); setIsSubjectOpen(false); }}
                              className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 block truncate transition cursor-pointer"
                            >
                              {getSubjectDisplayName(s)}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Date & Time */}
                <div className="space-y-1">
                  <label htmlFor="violation-date" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Date & Time</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      id="violation-date"
                      type="datetime-local"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 text-xs outline-none focus:ring-2 focus:ring-slate-400 transition font-medium text-slate-700"
                    />
                  </div>
                </div>
              </div>

              {/* Infractions Section */}
              <div className="space-y-1">
                <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Infraction Items</label>
                <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 border border-slate-200/80 rounded-xl">
                  {VIOLATION_OPTIONS.map(item => {
                    const isChecked = selectedViolations.includes(item);
                    return (
                      <label 
                        key={item} 
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border cursor-pointer transition select-none ${
                          isChecked 
                            ? 'bg-red-50 border-red-200 text-red-700 font-semibold' 
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleViolation(item)}
                          className="accent-red-600 rounded w-3 h-3"
                        />
                        <span>{item}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Actions Taken Section */}
              <div className="space-y-1">
                <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Actions Taken</label>
                <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 border border-slate-200/80 rounded-xl">
                  {INTERVENTION_OPTIONS.map(opt => {
                    const isChecked = interventions.includes(opt.key);
                    return (
                      <label 
                        key={opt.key} 
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border cursor-pointer transition select-none ${
                          isChecked 
                            ? 'bg-blue-50 border-blue-200 text-blue-700 font-semibold' 
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleIntervention(opt.key)}
                          className="accent-blue-600 rounded w-3 h-3"
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Row 2: Parent Alert & Notes in 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                {/* Parent Alert Box */}
                <div className="p-2.5 bg-amber-50/60 border border-amber-200/60 rounded-xl flex items-center gap-2.5 select-none">
                  <div className="p-1.5 bg-amber-100 text-amber-700 rounded-lg shrink-0">
                    <Bell className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={parentAlerted}
                        onChange={(e) => setParentAlerted(e.target.checked)}
                        className="accent-amber-600 rounded border-slate-300 w-3.5 h-3.5"
                      />
                      <span className="text-xs font-bold text-slate-800">Parent alerted admin</span>
                    </label>
                    <p className="text-[10px] text-slate-500 leading-tight mt-0.5 truncate">
                      Parents reached out directly.
                    </p>
                  </div>
                </div>

                {/* Term Info Box */}
                <div className="p-2.5 bg-slate-50 border border-slate-200/80 rounded-xl flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-xs">Term: <strong className="text-slate-800">{displayTermName}</strong></span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <label htmlFor="violation-notes" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Notes</label>
                <textarea
                  id="violation-notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional contextual details..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs outline-none focus:ring-2 focus:ring-slate-400 resize-none transition"
                />
              </div>

            </form>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="h-8 px-3.5 bg-white text-slate-600 font-mono text-xs font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="student-infraction-form"
                disabled={isSubmitting || selectedViolations.length === 0}
                className={`h-8 px-4 text-white text-xs font-mono font-semibold rounded-xl transition flex items-center gap-1.5 disabled:opacity-50 cursor-pointer ${
                  infractionData ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>{isSubmitting ? 'Saving...' : infractionData ? 'Update Record' : 'Save Record'}</span>
              </button>
            </div>

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}